import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  CloudArrowUpIcon as CloudUpIcon,
  DocumentIcon,
  FolderIcon,
} from '@heroicons/react/24/solid';
import * as React from 'react';

import { InspectArtifactButton } from '@/components/artifact-inspector/InspectArtifactButton';
import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGetBlobsQuery } from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import { CollectionNode } from '@/services/types/collection-types';

interface DocumentSelectionWithUploadProps {
  matterId: string;
  selectedBlobIds: Set<string>;
  onSelectionChange: (
    blobIds: Set<string> | ((prev: Set<string>) => Set<string>),
  ) => void;
  /** Optional filter function to limit which blobs are shown */
  filterBlobs?: (blob: any) => boolean;
  /** Optional custom title */
  title?: string;
  /** Optional custom description */
  description?: string;
  /** Callback when a blob is uploaded - used for recipient extraction */
  onBlobUploaded?: (blobId: string) => void;
  className?: string;
}

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  id: string;
  isRemoving?: boolean;
}

export const DocumentSelectionWithUpload: React.FC<
  DocumentSelectionWithUploadProps
> = ({
  matterId,
  selectedBlobIds,
  onSelectionChange,
  filterBlobs = (blob) => {
    // Default filter: PDF files only
    const isPdf =
      blob.file_extension?.toLowerCase() === 'pdf' ||
      blob.file_extension?.toLowerCase() === '.pdf' ||
      blob.file_mime_type?.toLowerCase() === 'application/pdf' ||
      blob.original_filename?.toLowerCase().endsWith('.pdf');
    return isPdf;
  },
  title = 'Select Documents',
  description = 'Choose or upload PDF documents to include',
  onBlobUploaded,
  className = '',
}) => {
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId, {
    skip: !matterId,
  });
  const { data: blobs, refetch: refetchBlobs } = useGetBlobsQuery(matterId, {
    skip: !matterId,
  });

  const [selectedCollectionId, setSelectedCollectionId] =
    React.useState<string>('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = React.useState<string>('select');

  // Use the upload manager hook
  const { handleUploadToCollection, handleUploadToCollectionAndReturn } =
    useUploadManagerUnified({
      matterId,
      refetch: refetchBlobs,
    });

  // Set root collection as default when data is loaded
  React.useEffect(() => {
    if (collectionTree && !selectedCollectionId) {
      setSelectedCollectionId(collectionTree.id);
    }
  }, [collectionTree, selectedCollectionId]);

  // Filter blobs based on selected collection and custom filter
  const filteredBlobs = React.useMemo(() => {
    if (!blobs) return [];

    return blobs.filter(
      (blob) =>
        filterBlobs(blob) &&
        (selectedCollectionId === collectionTree?.id ||
          blob.collection_details.id === selectedCollectionId) &&
        (searchQuery === '' ||
          blob.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          blob.original_filename
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase())),
    );
  }, [blobs, selectedCollectionId, collectionTree, searchQuery, filterBlobs]);

  // Get selected blob objects
  const selectedBlobs = React.useMemo(() => {
    if (!blobs) return [];
    return blobs.filter((blob) => selectedBlobIds.has(blob.id));
  }, [blobs, selectedBlobIds]);

  const handleToggleSelect = (blobId: string) => {
    // Use functional update to avoid stale closure issues
    onSelectionChange((prev: Set<string>) => {
      const newSelected = new Set(prev);
      if (newSelected.has(blobId)) {
        newSelected.delete(blobId);
      } else {
        newSelected.add(blobId);
        // Trigger recipient extraction for newly selected existing blob
        if (onBlobUploaded) {
          onBlobUploaded(blobId);
        }
      }
      return newSelected;
    });
  };

  const renderCollectionOptions = (
    node: CollectionNode,
    level = 0,
  ): JSX.Element[] => {
    const options: JSX.Element[] = [];
    const prefix = '\u00A0'.repeat(level * 2);

    options.push(
      <SelectItem
        key={node.id}
        value={node.id}
        className="cursor-pointer hover:bg-gray-100"
      >
        {prefix + (node.name === 'root' ? 'All Files' : node.name)}
      </SelectItem>,
    );

    if (node.children) {
      node.children.forEach((child: CollectionNode) => {
        options.push(...renderCollectionOptions(child, level + 1));
      });
    }

    return options;
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFiles = async (files: File[]) => {
    if (files.length === 0) return;

    // Add files to the upload queue with pending status
    const newUploadedFiles: UploadedFile[] = files.map((file) => ({
      file,
      status: 'pending' as const,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
    }));

    setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);

    // Upload all files in parallel
    const uploadPromises = newUploadedFiles.map(async (uploadedFile) => {
      try {
        // Update status to uploading
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === uploadedFile.id ? { ...f, status: 'uploading' } : f,
          ),
        );

        // Use handleUploadToCollectionAndReturn to get the created blob directly
        const createdBlobs = await handleUploadToCollectionAndReturn(
          [uploadedFile.file],
          selectedCollectionId,
        );

        // Update status to success
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === uploadedFile.id ? { ...f, status: 'success' } : f,
          ),
        );

        // If we got a blob back, select it directly using its ID
        if (createdBlobs && createdBlobs.length > 0) {
          const createdBlob = createdBlobs[0];
          // Use functional update to get latest state
          onSelectionChange((prevSelected: Set<string>) => {
            const newSelected = new Set(prevSelected);
            newSelected.add(createdBlob.id);
            return newSelected;
          });

          // Trigger recipient extraction callback if provided
          if (onBlobUploaded) {
            onBlobUploaded(createdBlob.id);
          }

          // Mark file as removing for animation after a short delay
          setTimeout(() => {
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.id === uploadedFile.id ? { ...f, isRemoving: true } : f,
              ),
            );

            // Actually remove after animation completes
            setTimeout(() => {
              setUploadedFiles((prev) =>
                prev.filter((f) => f.id !== uploadedFile.id),
              );
            }, 300);
          }, 1000); // Show success state before animating out
        } else {
          // Fallback: refetch and find by name if direct return failed
          await refetchBlobs();
          const { data: refreshedBlobs } = await refetchBlobs();
          if (refreshedBlobs) {
            const uploadedBlob = refreshedBlobs.find(
              (blob) => blob.name === uploadedFile.file.name,
            );
            if (uploadedBlob) {
              onSelectionChange((prevSelected: Set<string>) => {
                const newSelected = new Set(prevSelected);
                newSelected.add(uploadedBlob.id);
                return newSelected;
              });

              // Mark file as removing for animation
              setTimeout(() => {
                setUploadedFiles((prev) =>
                  prev.map((f) =>
                    f.id === uploadedFile.id ? { ...f, isRemoving: true } : f,
                  ),
                );

                // Actually remove after animation completes
                setTimeout(() => {
                  setUploadedFiles((prev) =>
                    prev.filter((f) => f.id !== uploadedFile.id),
                  );
                }, 300);
              }, 1000);
            }
          }
        }
      } catch (error) {
        // Update status to error
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === uploadedFile.id ? { ...f, status: 'error' } : f,
          ),
        );
        console.error('Upload failed:', error);
      }
    });

    await Promise.all(uploadPromises);
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      await handleUploadFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    await handleUploadFiles(droppedFiles);
  };

  const removeUploadedFile = (id: string) => {
    // Mark as removing for animation
    setUploadedFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, isRemoving: true } : f)),
    );

    // Actually remove after animation
    setTimeout(() => {
      setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
    }, 300);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'pending':
        return <CloudArrowUpIcon className="h-5 w-5 text-gray-400" />;
      case 'uploading':
        return (
          <CloudArrowUpIcon className="h-5 w-5 text-primary animate-pulse" />
        );
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XMarkIcon className="h-5 w-5 text-red-500" />;
      default:
        return <CloudArrowUpIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  return (
    <div className={`space-y-4 w-full overflow-hidden ${className}`}>
      <div>
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex-1 flex flex-col w-full overflow-hidden"
      >
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger
            value="select"
            className="data-[state=active]:bg-primary data-[state=active]:text-white"
          >
            Select Existing
          </TabsTrigger>
          <TabsTrigger
            value="upload"
            className="data-[state=active]:bg-primary data-[state=active]:text-white"
          >
            Upload New
          </TabsTrigger>
        </TabsList>

        <div className="mb-4">
          <Label className="text-sm font-semibold mb-2 block">
            Destination Folder
          </Label>
          <Select
            value={selectedCollectionId}
            onValueChange={(value) => setSelectedCollectionId(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a folder" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-300">
              {collectionTree && renderCollectionOptions(collectionTree)}
            </SelectContent>
          </Select>
        </div>

        <TabsContent
          value="select"
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="mb-4">
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>

          <div className="border rounded-lg max-h-96 overflow-y-auto overflow-x-hidden w-full">
            {filteredBlobs.length > 0 ? (
              <div className="divide-y w-full">
                {filteredBlobs.map((blob) => (
                  <div
                    key={blob.id}
                    className="flex items-center p-3 hover:bg-gray-50 transition-colors cursor-pointer w-full max-w-full overflow-hidden"
                    onClick={() => handleToggleSelect(blob.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggleSelect(blob.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Select ${blob.name}`}
                  >
                    <Checkbox
                      className="mr-3 flex-shrink-0 cursor-pointer data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      checked={selectedBlobIds.has(blob.id)}
                      onCheckedChange={() => handleToggleSelect(blob.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <DocumentIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate max-w-[300px]">
                        {blob.original_filename || blob.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatFileSize(blob.file_size)} •{' '}
                        {blob.file_extension?.toUpperCase().replace('.', '')}
                      </p>
                    </div>
                    {selectedBlobIds.has(blob.id) && (
                      <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 ml-2" />
                    )}
                    {blob.artifact_id && (
                      <InspectArtifactButton
                        artifactId={blob.artifact_id}
                        artifactType={ArtifactType.BLOB}
                        matterId={matterId}
                        compact
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-40 p-8 text-gray-500">
                <FolderIcon className="h-12 w-12 text-gray-300 mb-3" />
                <p className="text-sm font-medium">No documents found</p>
                <p className="text-xs mt-1">
                  {searchQuery
                    ? 'Try a different search'
                    : 'Upload some documents to get started'}
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent
          value="upload"
          className="flex-1 flex flex-col overflow-hidden"
        >
          {/* Upload Queue */}
          {uploadedFiles.length > 0 && (
            <div className="mb-4 animate-in slide-in-from-top-2 fade-in duration-300">
              <h3 className="text-sm font-semibold mb-2 text-gray-700">
                Upload Queue
              </h3>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto overflow-x-hidden bg-gray-50">
                <div className="space-y-2">
                  {uploadedFiles.map((uploadedFile) => (
                    <div
                      key={uploadedFile.id}
                      className={`flex items-center justify-between p-2 bg-white rounded-md border transition-all duration-300 ease-out
                        ${
                          uploadedFile.isRemoving
                            ? 'scale-95 opacity-0 translate-x-4'
                            : 'scale-100 opacity-100 translate-x-0 animate-in slide-in-from-left-2 fade-in duration-300'
                        }
                        ${
                          uploadedFile.status === 'success'
                            ? 'border-green-200 bg-green-50 animate-pulse'
                            : uploadedFile.status === 'error'
                              ? 'border-red-200 bg-red-50 animate-bounce'
                              : uploadedFile.status === 'uploading'
                                ? 'border-primary/30 bg-primary/5'
                                : 'border-gray-200'
                        }
                      `}
                    >
                      <div className="flex items-center min-w-0 flex-1 overflow-hidden">
                        <div
                          className={`flex-shrink-0 transition-transform duration-300 ${
                            uploadedFile.status === 'success'
                              ? 'scale-110'
                              : 'scale-100'
                          }`}
                        >
                          {getStatusIcon(uploadedFile.status)}
                        </div>
                        <div className="ml-2 min-w-0 flex-1 overflow-hidden">
                          <p className="text-sm truncate font-medium">
                            {uploadedFile.file.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {formatFileSize(uploadedFile.file.size)}
                          </p>
                        </div>
                      </div>
                      {uploadedFile.status !== 'uploading' && (
                        <button
                          type="button"
                          onClick={() => removeUploadedFile(uploadedFile.id)}
                          className="ml-2 text-gray-400 hover:text-red-500 transition-all duration-200 hover:scale-110"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Drag and Drop Area */}
          <div
            className={`flex-1 border-2 border-dashed rounded-lg transition-all duration-200 min-h-[200px] ${
              isDragging
                ? 'border-primary bg-primary/5 scale-[0.99]'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="h-full flex flex-col items-center justify-center p-8">
              <CloudUpIcon
                className={`h-16 w-16 mb-4 transition-colors ${
                  isDragging ? 'text-primary' : 'text-gray-300'
                }`}
              />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {isDragging ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p className="text-sm text-gray-500 mb-1">
                Files will upload automatically
              </p>
              <p className="text-sm text-gray-500 mb-4">or</p>
              <Button
                onClick={handleFileSelect}
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                Browse Files
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                multiple
                className="hidden"
                accept=".pdf,application/pdf"
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Selected Files Summary */}
      {selectedBlobs.length > 0 && (
        <div className="mt-4 pt-4 border-t overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold text-gray-700">
              Selected Documents ({selectedBlobs.length})
            </Label>
            {selectedBlobs.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSelectionChange(new Set())}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear all
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
            {selectedBlobs.map((blob) => (
              <div
                key={blob.id}
                className="flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm border border-primary/20 group hover:bg-primary/20 transition-colors max-w-full"
              >
                <DocumentIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span
                  className="truncate"
                  style={{ maxWidth: 'min(200px, 100%)' }}
                >
                  {blob.original_filename || blob.name}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleSelect(blob.id);
                  }}
                  className="ml-1 flex-shrink-0 text-primary/60 hover:text-red-500 transition-colors"
                  aria-label={`Remove ${blob.name}`}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
